/*
 *
 * XpayBackOfficePopup
 *
 * @version 1.0.0
 * @author Ivana Piscitelli
 * @since 10-Apr-2025
 */
import React, { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import { useIntl } from 'react-intl'
import MerchantModal from '@nexidigital/merchantmodal'
import formattedMessage from './languageModule'
import PortalSelect from '@nexidigital/portalselect'
import Toggle from 'Hoc/ToggleComponent'
import './XpayBackOfficePopup.scss'
import MerchantGenericCard from '@nexidigital/merchantgenericcard'
import { capitalizeAllString } from 'Utility/utils'

const XpayBackOfficePopup = (props) => {
  const intl = useIntl()

  const SHOPS = 'shops'
  const TERMINALS = 'terminals'

  const initialValues = {
    [SHOPS]: '',
    [TERMINALS]: ''
  }

  const validationMessages = {
    requiredError: intl.formatMessage(formattedMessage.requiredError.props)
  }

  const validationSchema = Yup.object().shape({
    [SHOPS]: Yup.string().required(validationMessages.requiredError),
    // We make the field always optional, validation will be handled during submit
    [TERMINALS]: Yup.string()
  })

  const shops = useMemo(
    () =>
      props.shops.length > 0
        ? props.shops.map((shop) => ({
          label: shop?.shopName && shop?.shopCode ? `${capitalizeAllString(shop.shopName)} - ${shop.shopCode}` : '',
          value: shop?.shopId || ''
        }))
        : [],
    [props.shops]
  )

  const terminals = useMemo(
    () =>
      props.terminals.map((terminal) => ({
        label: terminal?.terminalCode || '',
        value: terminal?.terminalCode || ''
      })),
    [props.terminals]
  )

  const handleSubmit = (values, { setSubmitting, setFieldError }) => {
    // Custom validation for the terminals field
    const isTerminalsDisabled = !values[SHOPS] || props.terminals.length === 0 || props.terminals.length === 1

    let hasErrors = false

    // If terminals are not disabled and the field is empty, show an error
    if (!isTerminalsDisabled && !values[TERMINALS]) {
      setFieldError(TERMINALS, validationMessages.requiredError)
      hasErrors = true
    }

    // Check for other existing errors (such as the one for the shops field)
    if (Object.keys(formik.errors).length > 0 && Object.keys(formik.touched).length > 0) {
      hasErrors = true
    }

    if (hasErrors) {
      setSubmitting(false)
      return
    }

    if (values.shops && (isTerminalsDisabled || values.terminals)) {
      const terminalCode = isTerminalsDisabled && props.terminals.length === 1
        ? props.terminals[0].terminalCode
        : values.terminals
      props.actions.doGetUrlForXpay(terminalCode)
    }
  }

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit
  })

  useEffect(() => {
    // if we have only one shop and no shop selected, we preselect the shop and disable the input
    if (props.shops.length === 1 && !formik.values[SHOPS]) {
      formik.setFieldValue(SHOPS, props.shops[0].shopId)
      formik.setFieldTouched(SHOPS, true, false)
      props.actions.doGetTerminals(props.shops[0].shopId)
    }
  }, [])

  useEffect(() => {
    // if we have only one terminal and shop is selected, we preselect the terminal and disable the input
    if (props.terminals.length === 1 && formik.values[SHOPS]) {
      formik.setFieldValue(TERMINALS, props.terminals[0].terminalCode)
      formik.setFieldTouched(TERMINALS, true, false)
    }
  }, [props.terminals])

  const handleDisableTerminals = () => {
    // if we have no shop selected or there's no terminals or we have only one terminal, we disable the input
    if (!formik.values[SHOPS] || props.terminals.length === 0) {
      return true
    }
    return props.terminals.length === 1
  }

  // if we have only one shop, we disable the input
  const handleDisableShops = () => props.shops.length === 1

  const onSelectShopChangeHandler = (e, fieldName) => {
    const value = e.value
    formik.setFieldValue(fieldName, value)
    formik.setFieldTouched(fieldName, true, false)

    formik.setFieldValue(TERMINALS, '')
    formik.setFieldTouched(TERMINALS, false, false)

    if (formik.errors[TERMINALS]) {
      formik.setFieldError(TERMINALS, undefined)
    }

    props.actions.doGetTerminals(value)
  }

  const onSelectTerminalChangeHandler = (e, fieldName) => {
    const value = e.value
    formik.setFieldValue(fieldName, value)
    formik.setFieldTouched(fieldName, true, false)
  }

  return (
    <Toggle>
      <MerchantModal
        primaryButtonLoading={props.isLoading}
        ariaModalDescription=''
        ariaModalTitle=''
        title={intl.formatMessage({ id: 't.app.services.xpayBackOffice.popup.title' })}
        onClose={() => props.actions.doHidePopup()}
        primaryAction={() => formik.handleSubmit()}
        primaryButtonLabel={intl.formatMessage({ id: 't.app.login' })}
        customAnalyticsDescription={formattedMessage.description}
      >
        <MerchantGenericCard title={formattedMessage.description}>
          <div className='xpayBackOfficePopup'>
            <PortalSelect
              noSearch
              id={SHOPS}
              name={SHOPS}
              label={intl.formatMessage({ id: 't.app.shop.companyName' })}
              placeholder={intl.formatMessage({ id: 't.app.services.xpayBackOffice.popup.placeholderPuntoVendita' })}
              options={shops}
              value={formik.values[SHOPS]}
              {...formik.getFieldProps(SHOPS)}
              error={formik.touched[SHOPS] && !formik.values[SHOPS] && !!formik.errors[SHOPS]}
              errorText={(formik.touched[SHOPS] && !formik.values[SHOPS] && formik.errors[SHOPS]) || ''}
              onChange={(e) => onSelectShopChangeHandler(e, SHOPS)}
              disabled={handleDisableShops()}
            />
            <PortalSelect
              noSearch
              id={TERMINALS}
              name={TERMINALS}
              label={intl.formatMessage({ id: 't.app.services.xpayBackOffice.popup.labelTerminal' })}
              placeholder={intl.formatMessage({ id: 't.app.services.xpayBackOffice.popup.placeholderTerminal' })}
              options={terminals}
              value={formik.values[TERMINALS]}
              {...formik.getFieldProps(TERMINALS)}
              error={!handleDisableTerminals() && formik.touched[TERMINALS] && !!formik.errors[TERMINALS]}
              errorText={(!handleDisableTerminals() && formik.touched[TERMINALS] && formik.errors[TERMINALS]) || ''}
              onChange={(e) => onSelectTerminalChangeHandler(e, TERMINALS)}
              disabled={handleDisableTerminals()}
            />
          </div>
        </MerchantGenericCard>
      </MerchantModal>
    </Toggle>
  )
}

XpayBackOfficePopup.propTypes = {
  actions: PropTypes.object.isRequired,
  shops: PropTypes.array,
  terminals: PropTypes.array,
  isLoading: PropTypes.bool
}

XpayBackOfficePopup.defaultProps = {
  actions: {},
  shops: [],
  terminals: [],
  isLoading: false
}

export default XpayBackOfficePopup
